import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { NumericFormat } from "react-number-format";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EuroIcon from "@mui/icons-material/Euro";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import PersonIcon from "@mui/icons-material/Person";
import { italianProvinces } from "../../../cvt/data/zoneMap";
import {
  DEFAULT_GAP_LIMIT,
  DEFAULT_GAP_LIMITS,
  DEFAULT_DURATIONS,
} from "../../../cvt/data/satellitare";

const formatLimit = (limit) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(limit);

// La provincia del cliente arriva come sigla (o, in alcuni tracciati, come nome):
// si accettano entrambe per precompilare il campo
export const findProvince = (value) => {
  if (!value) return null;
  const raw = String(value).trim().toLowerCase();
  return (
    italianProvinces.find((p) => p.code.toLowerCase() === raw) ||
    italianProvinces.find((p) => p.name.toLowerCase() === raw) ||
    null
  );
};

const provinceLabel = (option) => `${option.code} - ${option.name}`;

// Testo digitato a mano nel campo: oltre a sigla e nome si accetta anche
// l'etichetta completa mostrata dall'Autocomplete ("MI - Milano")
const findProvinceByInput = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw) return null;
  return (
    findProvince(raw) ||
    italianProvinces.find((p) => provinceLabel(p).toLowerCase() === raw) ||
    null
  );
};

const initialFormData = (defaultProvince) => ({
  provinceCode: defaultProvince?.code || "",
  province: defaultProvince?.name || "",
  vehicleValue: "",
  duration: "24",
  gapLimit: DEFAULT_GAP_LIMIT,
});

/**
 * Form ridotto ai soli campi richiesti da `dealerQuotation`: gli altri dati
 * (targa, marca, modello) sono già quelli della pratica aperta.
 */
export default function CVTQuickQuote({
  onCalculate,
  loading,
  defaultProvinceCode,
}) {
  const [formData, setFormData] = useState(() =>
    initialFormData(findProvince(defaultProvinceCode)),
  );
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Sigla e nome si aggiornano sempre insieme, e solo da una provincia valida:
  // il testo digitato non entra mai in formData, così non possono divergere
  const selectProvince = (option) => {
    setFormData((prev) => ({
      ...prev,
      provinceCode: option?.code || "",
      province: option?.name || "",
    }));
  };

  const selectedProvinceOption =
    italianProvinces.find((p) => p.code === formData.provinceCode) || null;

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.provinceCode) {
      newErrors.province = "Seleziona la provincia";
    }
    if (!formData.vehicleValue || parseFloat(formData.vehicleValue) <= 0) {
      newErrors.vehicleValue = "Inserisci un valore veicolo valido";
    }
    if (!formData.duration) {
      newErrors.duration = "Seleziona la durata";
    }
    if (!formData.gapLimit) {
      newErrors.gapLimit = "Seleziona il massimale GAP";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;
    return onCalculate(formData);
  };

  return (
    <Accordion sx={{ mb: 4 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography fontWeight={700}>Modifica preventivo</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          columns={{ xs: 12, sm: 12, md: 12 }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              fullWidth
              options={italianProvinces}
              value={selectedProvinceOption}
              size="small"
              disablePortal
              ListboxProps={{ sx: { maxHeight: 220, overflowY: "auto" } }}
              noOptionsText="Nessuna provincia trovata"
              isOptionEqualToValue={(option, value) =>
                option.code === value?.code
              }
              getOptionLabel={(option) =>
                typeof option === "string" ? option : provinceLabel(option)
              }
              onChange={(_, newValue) => selectProvince(newValue)}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "input" && !newInputValue) selectProvince(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // Se si scrive una provincia per intero e si esce dal campo
                  // (es. cliccando "Calcola preventivo") senza sceglierla dalla
                  // lista, la si seleziona; altrimenti l'Autocomplete ripristina
                  // il testo della provincia selezionata
                  inputProps={{
                    ...params.inputProps,
                    onBlur: (event) => {
                      const match = findProvinceByInput(event.target.value);
                      if (match && match.code !== formData.provinceCode) {
                        selectProvince(match);
                      }
                      params.inputProps.onBlur?.(event);
                    },
                  }}
                  fullWidth
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationOnIcon fontSize="small" /> Provincia *
                    </Box>
                  }
                  error={!!errors.province}
                  helperText={errors.province}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <span>{option.name}</span>
                    <Typography variant="body2" color="text.secondary">
                      {option.code}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <NumericFormat
              customInput={TextField}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              allowNegative={false}
              valueIsNumericString
              fullWidth
              size="small"
              placeholder="25.000"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EuroIcon fontSize="small" /> Valore veicolo *
                </Box>
              }
              value={formData.vehicleValue}
              // in formData resta il valore numerico non formattato ("25000.5")
              onValueChange={({ value }) => handleChange("vehicleValue", value)}
              error={!!errors.vehicleValue}
              helperText={errors.vehicleValue}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    €
                  </Typography>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayIcon fontSize="small" /> Durata
                </Box>
              }
              value={formData.duration}
              onChange={(e) => handleChange("duration", e.target.value)}
              error={!!errors.duration}
              helperText={errors.duration}
            >
              {DEFAULT_DURATIONS.map((months) => (
                <MenuItem key={months} value={String(months)}>
                  {months} mesi
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SatelliteAltIcon fontSize="small" /> Massimale
                </Box>
              }
              value={formData.gapLimit}
              onChange={(e) => handleChange("gapLimit", e.target.value)}
              error={!!errors.gapLimit}
              helperText={errors.gapLimit}
            >
              {DEFAULT_GAP_LIMITS.map((limit) => (
                <MenuItem key={limit} value={String(limit)}>
                  {formatLimit(limit)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={15}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={<SearchIcon />}
                sx={{ fontWeight: 700, minWidth: 160 }}
              >
                Calcola preventivo
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
